import { z } from 'zod';

/**
 * Mirrors `PrismaEnums.TeacherAttendancePolicyActionLevel` without importing
 * `@airreading/common` (its barrel pulls `@airreading/common/data` and hits a
 * coach-payment circular import under Vitest `@airreading/source`).
 */
export const TeacherAttendancePolicyActionLevel = {
  within_allowance: 'within_allowance',
  warning: 'warning',
  reassignment_eligible: 'reassignment_eligible',
  final_warning: 'final_warning',
  third_no_show: 'third_no_show',
  third_late_entry: 'third_late_entry',
  third_early_exit: 'third_early_exit',
  final_14_day_protected: 'final_14_day_protected',
} as const;
export type TeacherAttendancePolicyActionLevel =
  (typeof TeacherAttendancePolicyActionLevel)[keyof typeof TeacherAttendancePolicyActionLevel];

export const TeacherAttendancePolicySecondaryStatus = {
  teacher_no_show: 'teacher_no_show',
  late_entries_1: 'late_entries_1',
  late_entries_2: 'late_entries_2',
  late_entries_3: 'late_entries_3',
  late_entries_4_plus: 'late_entries_4_plus',
  early_exits_1: 'early_exits_1',
  early_exits_2: 'early_exits_2',
  early_exits_3: 'early_exits_3',
  early_exits_4_plus: 'early_exits_4_plus',
  total_substitutes_1_remaining: 'total_substitutes_1_remaining',
  total_substitutes_at_allowance: 'total_substitutes_at_allowance',
  total_substitutes_1_over: 'total_substitutes_1_over',
  total_substitutes_2_plus_over: 'total_substitutes_2_plus_over',
  short_notice_1_remaining: 'short_notice_1_remaining',
  short_notice_at_allowance: 'short_notice_at_allowance',
  short_notice_1_over: 'short_notice_1_over',
  short_notice_2_plus_over: 'short_notice_2_plus_over',
} as const;
export type TeacherAttendancePolicySecondaryStatus =
  (typeof TeacherAttendancePolicySecondaryStatus)[keyof typeof TeacherAttendancePolicySecondaryStatus];

type AttendancePolicyV2OverviewUsage = {
  totalSubRequestUsage: number;
  totalSubRequestAllowance: number;
  shortNoticeSubRequestUsage: number;
  shortNoticeSubRequestAllowance: number;
};

/** Counts + substitute usage shown in the admin V2 attendance summary column. */
type AttendancePolicyV2OverviewSummary = AttendancePolicyV2OverviewUsage & {
  noShowCount: number;
  lateEntryCount: number;
  earlyExitCount: number;
};

type AttendancePolicyV2HistoryEventKind =
  | 'no_show'
  | 'late_entry'
  | 'early_exit'
  | 'regular_sub_request'
  | 'short_notice_sub_request'
  | 'exemption'
  | 'technical_failure'
  | 'uncounted_sub_request';

export function formatAttendancePolicyV2Limit({
  totalSubRequestAllowance,
  shortNoticeSubRequestAllowance,
}: {
  totalSubRequestAllowance: number;
  shortNoticeSubRequestAllowance: number;
}): string {
  const classLabel = totalSubRequestAllowance === 1 ? 'class' : 'classes';
  return `${totalSubRequestAllowance} ${classLabel} (max ${shortNoticeSubRequestAllowance} short-notice)`;
}

export function formatAttendancePolicyV2UsageFraction(usage: number, allowance: number): string {
  return `${usage} / ${allowance}`;
}

/**
 * Informational V2 attendance summary lines for the admin table column.
 * Omits dimensions with zero count/usage; returns an empty array when all are zero
 * (caller should render "-").
 */
export function formatAttendancePolicyV2OverviewLines(policy: AttendancePolicyV2OverviewSummary): string[] {
  const {
    noShowCount,
    lateEntryCount,
    earlyExitCount,
    totalSubRequestUsage,
    totalSubRequestAllowance,
    shortNoticeSubRequestUsage,
    shortNoticeSubRequestAllowance,
  } = policy;

  const lines: string[] = [];

  if (noShowCount > 0) {
    lines.push(`Teacher no-shows: ${noShowCount}`);
  }
  if (lateEntryCount > 0) {
    lines.push(`Late entries: ${lateEntryCount}`);
  }
  if (earlyExitCount > 0) {
    lines.push(`Early exits: ${earlyExitCount}`);
  }
  if (totalSubRequestUsage > 0) {
    lines.push(
      `Total substitutes: ${formatAttendancePolicyV2UsageFraction(totalSubRequestUsage, totalSubRequestAllowance)}`,
    );
  }
  if (shortNoticeSubRequestUsage > 0) {
    lines.push(
      `Short notice: ${formatAttendancePolicyV2UsageFraction(
        shortNoticeSubRequestUsage,
        shortNoticeSubRequestAllowance,
      )}`,
    );
  }

  return lines;
}

/** Phrase for one allowance dimension: remaining, exactly at the cap, or over it. */
export function formatAttendancePolicyV2DimensionStatus(usage: number, allowance: number): string {
  const remaining = allowance - usage;
  if (remaining > 0) {
    return `${remaining} remaining`;
  }
  if (remaining === 0) {
    return 'At allowance';
  }
  return 'Over allowance';
}

export function formatAttendancePolicyV2SecondaryStatusLabels(policy: AttendancePolicyV2OverviewUsage): {
  totalSubstitutes: string;
  shortNotice: string;
} {
  return {
    totalSubstitutes: `Total substitutes: ${formatAttendancePolicyV2DimensionStatus(
      policy.totalSubRequestUsage,
      policy.totalSubRequestAllowance,
    )}`,
    shortNotice: `Short notice: ${formatAttendancePolicyV2DimensionStatus(
      policy.shortNoticeSubRequestUsage,
      policy.shortNoticeSubRequestAllowance,
    )}`,
  };
}

/** High-level statuses written by AIR-3215 calculations. */
export type AttendancePolicyV2HighLevelStatus =
  | typeof TeacherAttendancePolicyActionLevel.within_allowance
  | typeof TeacherAttendancePolicyActionLevel.warning
  | typeof TeacherAttendancePolicyActionLevel.reassignment_eligible;

/** Counters/allowances needed to apply AIR-3215 thresholds on unbackfilled rows. */
export type AttendancePolicyV2EscalationCounts = {
  noShowCount: number;
  lateEntryCount: number;
  earlyExitCount: number;
  totalSubRequestUsage: number;
  totalSubRequestAllowance: number;
  shortNoticeSubRequestUsage: number;
  shortNoticeSubRequestAllowance: number;
};

/** Keep in sync with server ATTENDANCE_POLICY_V2 assignment late/early + no-show thresholds. */
const NO_SHOW_REASSIGNMENT_THRESHOLD = 1;
const ASSIGNMENT_LATE_EARLY_WARNING_MAX = 3;
const ASSIGNMENT_LATE_EARLY_REASSIGNMENT_THRESHOLD = 4;

const HIGH_LEVEL_SEVERITY_RANK: Record<AttendancePolicyV2HighLevelStatus, number> = {
  [TeacherAttendancePolicyActionLevel.within_allowance]: 0,
  [TeacherAttendancePolicyActionLevel.warning]: 1,
  [TeacherAttendancePolicyActionLevel.reassignment_eligible]: 2,
};

function raiseHighLevel(
  current: AttendancePolicyV2HighLevelStatus,
  next: AttendancePolicyV2HighLevelStatus,
): AttendancePolicyV2HighLevelStatus {
  return HIGH_LEVEL_SEVERITY_RANK[next] > HIGH_LEVEL_SEVERITY_RANK[current] ? next : current;
}

function substituteDimensionHighLevel(usage: number, allowance: number): AttendancePolicyV2HighLevelStatus | null {
  const remaining = allowance - usage;
  if (remaining >= 2) {
    return null;
  }
  if (remaining <= -2) {
    return TeacherAttendancePolicyActionLevel.reassignment_eligible;
  }
  return TeacherAttendancePolicyActionLevel.warning;
}

function lateEarlyDimensionHighLevel(count: number): AttendancePolicyV2HighLevelStatus | null {
  if (!Number.isFinite(count) || count <= 0) {
    return null;
  }
  if (count >= ASSIGNMENT_LATE_EARLY_REASSIGNMENT_THRESHOLD) {
    return TeacherAttendancePolicyActionLevel.reassignment_eligible;
  }
  if (count <= ASSIGNMENT_LATE_EARLY_WARNING_MAX) {
    return TeacherAttendancePolicyActionLevel.warning;
  }
  return TeacherAttendancePolicyActionLevel.reassignment_eligible;
}

/**
 * Applies AIR-3215 thresholds from persisted counts. Prefer this over mapping
 * legacy `actionLevel` alone for unbackfilled snapshots.
 */
export function attendancePolicyV2HighLevelStatusFromCounts(
  counts: AttendancePolicyV2EscalationCounts,
): AttendancePolicyV2HighLevelStatus {
  let highLevel: AttendancePolicyV2HighLevelStatus = TeacherAttendancePolicyActionLevel.within_allowance;

  if (Number.isFinite(counts.noShowCount) && counts.noShowCount >= NO_SHOW_REASSIGNMENT_THRESHOLD) {
    highLevel = raiseHighLevel(highLevel, TeacherAttendancePolicyActionLevel.reassignment_eligible);
  }

  const lateLevel = lateEarlyDimensionHighLevel(counts.lateEntryCount);
  if (lateLevel) {
    highLevel = raiseHighLevel(highLevel, lateLevel);
  }
  const earlyLevel = lateEarlyDimensionHighLevel(counts.earlyExitCount);
  if (earlyLevel) {
    highLevel = raiseHighLevel(highLevel, earlyLevel);
  }

  const totalLevel = substituteDimensionHighLevel(counts.totalSubRequestUsage, counts.totalSubRequestAllowance);
  if (totalLevel) {
    highLevel = raiseHighLevel(highLevel, totalLevel);
  }
  const shortNoticeLevel = substituteDimensionHighLevel(
    counts.shortNoticeSubRequestUsage,
    counts.shortNoticeSubRequestAllowance,
  );
  if (shortNoticeLevel) {
    highLevel = raiseHighLevel(highLevel, shortNoticeLevel);
  }

  return highLevel;
}

export type AttendancePolicyV2StatusBadgeModel = {
  actionLevel: TeacherAttendancePolicyActionLevel;
  secondaryStatuses: TeacherAttendancePolicySecondaryStatus[];
  escalationCounts?: AttendancePolicyV2EscalationCounts;
};

/**
 * Badge inputs for the admin attendance status. A teacher drop shows Within allowance
 * and hides secondary flags; counts are omitted so they cannot raise the high-level status.
 */
export function resolveAttendancePolicyV2StatusBadge(input: {
  actionLevel: TeacherAttendancePolicyActionLevel;
  secondaryStatuses?: TeacherAttendancePolicySecondaryStatus[];
  escalationCounts?: AttendancePolicyV2EscalationCounts;
  attendanceFlagsSuppressed?: boolean;
}): AttendancePolicyV2StatusBadgeModel {
  if (input.attendanceFlagsSuppressed) {
    return {
      actionLevel: TeacherAttendancePolicyActionLevel.within_allowance,
      secondaryStatuses: [],
    };
  }

  return {
    actionLevel: input.actionLevel,
    secondaryStatuses: input.secondaryStatuses ?? [],
    ...(input.escalationCounts ? { escalationCounts: input.escalationCounts } : {}),
  };
}

/**
 * Resolves the Ops-facing high-level badge. When counts are provided, uses AIR-3215
 * thresholds (correct for unbackfilled legacy rows). Otherwise falls back to mapping
 * persisted/legacy `actionLevel` labels.
 */
export function toAttendancePolicyV2HighLevelStatus(
  actionLevel: TeacherAttendancePolicyActionLevel,
  counts?: AttendancePolicyV2EscalationCounts,
): AttendancePolicyV2HighLevelStatus {
  if (counts) {
    return attendancePolicyV2HighLevelStatusFromCounts(counts);
  }

  switch (actionLevel) {
    case TeacherAttendancePolicyActionLevel.within_allowance:
      return TeacherAttendancePolicyActionLevel.within_allowance;
    case TeacherAttendancePolicyActionLevel.warning:
    case TeacherAttendancePolicyActionLevel.final_warning:
      return TeacherAttendancePolicyActionLevel.warning;
    case TeacherAttendancePolicyActionLevel.reassignment_eligible:
    case TeacherAttendancePolicyActionLevel.third_no_show:
    case TeacherAttendancePolicyActionLevel.third_late_entry:
    case TeacherAttendancePolicyActionLevel.third_early_exit:
    case TeacherAttendancePolicyActionLevel.final_14_day_protected:
      return TeacherAttendancePolicyActionLevel.reassignment_eligible;
  }
}

export const AttendancePolicyV2HighLevelColorMap: Record<AttendancePolicyV2HighLevelStatus, string> = {
  [TeacherAttendancePolicyActionLevel.within_allowance]: 'bg-[#DCFCE7] hover:bg-[#DCFCE7] text-[#166534]',
  [TeacherAttendancePolicyActionLevel.warning]: 'bg-[#FFEDD5] hover:bg-[#FFEDD5] text-[#EA580C]',
  [TeacherAttendancePolicyActionLevel.reassignment_eligible]: 'bg-[#FEE2E2] hover:bg-[#FEE2E2] text-[#DC2626]',
};

export const AttendancePolicyV2HighLevelLabelMap: Record<AttendancePolicyV2HighLevelStatus, string> = {
  [TeacherAttendancePolicyActionLevel.within_allowance]: 'Within allowance',
  [TeacherAttendancePolicyActionLevel.warning]: 'Warning',
  [TeacherAttendancePolicyActionLevel.reassignment_eligible]: 'Reassignment eligible',
};

/** @deprecated Prefer `AttendancePolicyV2HighLevelColorMap` + `toAttendancePolicyV2HighLevelStatus`. */
export const AttendancePolicyV2ActionLevelColorMap: Record<TeacherAttendancePolicyActionLevel, string> = {
  [TeacherAttendancePolicyActionLevel.within_allowance]:
    AttendancePolicyV2HighLevelColorMap[TeacherAttendancePolicyActionLevel.within_allowance],
  [TeacherAttendancePolicyActionLevel.warning]:
    AttendancePolicyV2HighLevelColorMap[TeacherAttendancePolicyActionLevel.warning],
  [TeacherAttendancePolicyActionLevel.final_warning]:
    AttendancePolicyV2HighLevelColorMap[TeacherAttendancePolicyActionLevel.warning],
  [TeacherAttendancePolicyActionLevel.reassignment_eligible]:
    AttendancePolicyV2HighLevelColorMap[TeacherAttendancePolicyActionLevel.reassignment_eligible],
  [TeacherAttendancePolicyActionLevel.third_no_show]:
    AttendancePolicyV2HighLevelColorMap[TeacherAttendancePolicyActionLevel.reassignment_eligible],
  [TeacherAttendancePolicyActionLevel.third_late_entry]:
    AttendancePolicyV2HighLevelColorMap[TeacherAttendancePolicyActionLevel.reassignment_eligible],
  [TeacherAttendancePolicyActionLevel.third_early_exit]:
    AttendancePolicyV2HighLevelColorMap[TeacherAttendancePolicyActionLevel.reassignment_eligible],
  [TeacherAttendancePolicyActionLevel.final_14_day_protected]:
    AttendancePolicyV2HighLevelColorMap[TeacherAttendancePolicyActionLevel.reassignment_eligible],
};

/** @deprecated Prefer `AttendancePolicyV2HighLevelLabelMap` + `toAttendancePolicyV2HighLevelStatus`. */
export const AttendancePolicyV2ActionLevelLabelMap: Record<TeacherAttendancePolicyActionLevel, string> = {
  [TeacherAttendancePolicyActionLevel.within_allowance]:
    AttendancePolicyV2HighLevelLabelMap[TeacherAttendancePolicyActionLevel.within_allowance],
  [TeacherAttendancePolicyActionLevel.warning]:
    AttendancePolicyV2HighLevelLabelMap[TeacherAttendancePolicyActionLevel.warning],
  [TeacherAttendancePolicyActionLevel.final_warning]:
    AttendancePolicyV2HighLevelLabelMap[TeacherAttendancePolicyActionLevel.warning],
  [TeacherAttendancePolicyActionLevel.reassignment_eligible]:
    AttendancePolicyV2HighLevelLabelMap[TeacherAttendancePolicyActionLevel.reassignment_eligible],
  [TeacherAttendancePolicyActionLevel.third_no_show]:
    AttendancePolicyV2HighLevelLabelMap[TeacherAttendancePolicyActionLevel.reassignment_eligible],
  [TeacherAttendancePolicyActionLevel.third_late_entry]:
    AttendancePolicyV2HighLevelLabelMap[TeacherAttendancePolicyActionLevel.reassignment_eligible],
  [TeacherAttendancePolicyActionLevel.third_early_exit]:
    AttendancePolicyV2HighLevelLabelMap[TeacherAttendancePolicyActionLevel.reassignment_eligible],
  [TeacherAttendancePolicyActionLevel.final_14_day_protected]:
    AttendancePolicyV2HighLevelLabelMap[TeacherAttendancePolicyActionLevel.reassignment_eligible],
};

export const AttendancePolicyV2SecondaryStatusLabelMap: Record<TeacherAttendancePolicySecondaryStatus, string> = {
  [TeacherAttendancePolicySecondaryStatus.teacher_no_show]: 'Teacher no-show',
  [TeacherAttendancePolicySecondaryStatus.late_entries_1]: 'Late entries: 1',
  [TeacherAttendancePolicySecondaryStatus.late_entries_2]: 'Late entries: 2',
  [TeacherAttendancePolicySecondaryStatus.late_entries_3]: 'Late entries: 3',
  [TeacherAttendancePolicySecondaryStatus.late_entries_4_plus]: 'Late entries: 4+',
  [TeacherAttendancePolicySecondaryStatus.early_exits_1]: 'Early exits: 1',
  [TeacherAttendancePolicySecondaryStatus.early_exits_2]: 'Early exits: 2',
  [TeacherAttendancePolicySecondaryStatus.early_exits_3]: 'Early exits: 3',
  [TeacherAttendancePolicySecondaryStatus.early_exits_4_plus]: 'Early exits: 4+',
  [TeacherAttendancePolicySecondaryStatus.total_substitutes_1_remaining]: 'Total substitutes: 1 remaining',
  [TeacherAttendancePolicySecondaryStatus.total_substitutes_at_allowance]: 'Total substitutes: At allowance',
  [TeacherAttendancePolicySecondaryStatus.total_substitutes_1_over]: 'Total substitutes: 1 over',
  [TeacherAttendancePolicySecondaryStatus.total_substitutes_2_plus_over]: 'Total substitutes: 2+ over',
  [TeacherAttendancePolicySecondaryStatus.short_notice_1_remaining]: 'Short notice: 1 remaining',
  [TeacherAttendancePolicySecondaryStatus.short_notice_at_allowance]: 'Short notice: At allowance',
  [TeacherAttendancePolicySecondaryStatus.short_notice_1_over]: 'Short notice: 1 over',
  [TeacherAttendancePolicySecondaryStatus.short_notice_2_plus_over]: 'Short notice: 2+ over',
};

/**
 * Per-secondary severity for badge tint. Warning-tier secondaries stay orange even when
 * the assignment high-level status is Reassignment eligible (Product / AIR-3215 QA).
 */
export const AttendancePolicyV2SecondaryStatusSeverity: Record<
  TeacherAttendancePolicySecondaryStatus,
  typeof TeacherAttendancePolicyActionLevel.warning | typeof TeacherAttendancePolicyActionLevel.reassignment_eligible
> = {
  [TeacherAttendancePolicySecondaryStatus.teacher_no_show]: TeacherAttendancePolicyActionLevel.reassignment_eligible,
  [TeacherAttendancePolicySecondaryStatus.late_entries_1]: TeacherAttendancePolicyActionLevel.warning,
  [TeacherAttendancePolicySecondaryStatus.late_entries_2]: TeacherAttendancePolicyActionLevel.warning,
  [TeacherAttendancePolicySecondaryStatus.late_entries_3]: TeacherAttendancePolicyActionLevel.warning,
  [TeacherAttendancePolicySecondaryStatus.late_entries_4_plus]:
    TeacherAttendancePolicyActionLevel.reassignment_eligible,
  [TeacherAttendancePolicySecondaryStatus.early_exits_1]: TeacherAttendancePolicyActionLevel.warning,
  [TeacherAttendancePolicySecondaryStatus.early_exits_2]: TeacherAttendancePolicyActionLevel.warning,
  [TeacherAttendancePolicySecondaryStatus.early_exits_3]: TeacherAttendancePolicyActionLevel.warning,
  [TeacherAttendancePolicySecondaryStatus.early_exits_4_plus]: TeacherAttendancePolicyActionLevel.reassignment_eligible,
  [TeacherAttendancePolicySecondaryStatus.total_substitutes_1_remaining]: TeacherAttendancePolicyActionLevel.warning,
  [TeacherAttendancePolicySecondaryStatus.total_substitutes_at_allowance]: TeacherAttendancePolicyActionLevel.warning,
  [TeacherAttendancePolicySecondaryStatus.total_substitutes_1_over]: TeacherAttendancePolicyActionLevel.warning,
  [TeacherAttendancePolicySecondaryStatus.total_substitutes_2_plus_over]:
    TeacherAttendancePolicyActionLevel.reassignment_eligible,
  [TeacherAttendancePolicySecondaryStatus.short_notice_1_remaining]: TeacherAttendancePolicyActionLevel.warning,
  [TeacherAttendancePolicySecondaryStatus.short_notice_at_allowance]: TeacherAttendancePolicyActionLevel.warning,
  [TeacherAttendancePolicySecondaryStatus.short_notice_1_over]: TeacherAttendancePolicyActionLevel.warning,
  [TeacherAttendancePolicySecondaryStatus.short_notice_2_plus_over]:
    TeacherAttendancePolicyActionLevel.reassignment_eligible,
};

/** Badge tint by secondary severity (not by assignment high-level status). */
export const AttendancePolicyV2SecondaryStatusColorMap: Record<
  typeof TeacherAttendancePolicyActionLevel.warning | typeof TeacherAttendancePolicyActionLevel.reassignment_eligible,
  string
> = {
  [TeacherAttendancePolicyActionLevel.warning]: 'bg-[#FFEDD5]/80 hover:bg-[#FFEDD5]/80 text-[#A16207]',
  [TeacherAttendancePolicyActionLevel.reassignment_eligible]: 'bg-[#FEE2E2]/80 hover:bg-[#FEE2E2]/80 text-[#B91C1C]',
};

export function attendancePolicyV2SecondaryStatusColor(secondary: TeacherAttendancePolicySecondaryStatus): string {
  return AttendancePolicyV2SecondaryStatusColorMap[AttendancePolicyV2SecondaryStatusSeverity[secondary]];
}

/** Ops filter options aligned with high-level badges (AIR-3215). */
export const attendancePolicyV2ActionLevelFilterGroupSchema = z.enum([
  TeacherAttendancePolicyActionLevel.within_allowance,
  TeacherAttendancePolicyActionLevel.warning,
  TeacherAttendancePolicyActionLevel.reassignment_eligible,
]);
export type AttendancePolicyV2ActionLevelFilterGroup = z.infer<typeof attendancePolicyV2ActionLevelFilterGroupSchema>;
export const AttendancePolicyV2ActionLevelFilterGroup = attendancePolicyV2ActionLevelFilterGroupSchema.enum;

export const AttendancePolicyV2ActionLevelFilterGroupLabelMap: Record<
  AttendancePolicyV2ActionLevelFilterGroup,
  string
> = {
  [AttendancePolicyV2ActionLevelFilterGroup.within_allowance]: 'Within allowance',
  [AttendancePolicyV2ActionLevelFilterGroup.warning]: 'Warning',
  [AttendancePolicyV2ActionLevelFilterGroup.reassignment_eligible]: 'Reassignment eligible',
};

/**
 * Expand filter selections to actionLevel enum values (incl. legacy). The server maps
 * these onto AIR-3215 high-level groups and filters by persisted counters so list
 * badges stay aligned when writes are still gated to legacy actionLevel values.
 */
export const AttendancePolicyV2ActionLevelFilterGroupToActionLevels: Record<
  AttendancePolicyV2ActionLevelFilterGroup,
  TeacherAttendancePolicyActionLevel[]
> = {
  [AttendancePolicyV2ActionLevelFilterGroup.within_allowance]: [TeacherAttendancePolicyActionLevel.within_allowance],
  [AttendancePolicyV2ActionLevelFilterGroup.warning]: [
    TeacherAttendancePolicyActionLevel.warning,
    TeacherAttendancePolicyActionLevel.final_warning,
  ],
  [AttendancePolicyV2ActionLevelFilterGroup.reassignment_eligible]: [
    TeacherAttendancePolicyActionLevel.reassignment_eligible,
    TeacherAttendancePolicyActionLevel.third_no_show,
    TeacherAttendancePolicyActionLevel.third_late_entry,
    TeacherAttendancePolicyActionLevel.third_early_exit,
    TeacherAttendancePolicyActionLevel.final_14_day_protected,
  ],
};

export const attendancePolicyV2ActionLevelFilterGroupOptions = (
  Object.keys(AttendancePolicyV2ActionLevelFilterGroupLabelMap) as AttendancePolicyV2ActionLevelFilterGroup[]
).map((value) => ({
  label: AttendancePolicyV2ActionLevelFilterGroupLabelMap[value],
  value,
}));

export function expandAttendancePolicyV2ActionLevelFilterGroups(
  groups: AttendancePolicyV2ActionLevelFilterGroup[] | undefined,
): TeacherAttendancePolicyActionLevel[] | undefined {
  if (!groups?.length) {
    return undefined;
  }

  return [...new Set(groups.flatMap((group) => AttendancePolicyV2ActionLevelFilterGroupToActionLevels[group]))];
}

export const AttendancePolicyV2HistoryEventBadgeColorMap: Record<AttendancePolicyV2HistoryEventKind, string> = {
  no_show: 'bg-[#FEE2E2] hover:bg-[#FEE2E2] text-[#DC2626]',
  late_entry: 'bg-[#FFEDD5] hover:bg-[#FFEDD5] text-[#EA580C]',
  early_exit: 'bg-[#FFEDD5] hover:bg-[#FFEDD5] text-[#EA580C]',
  regular_sub_request: 'bg-[#DBEAFE] hover:bg-[#DBEAFE] text-[#1D4ED8]',
  short_notice_sub_request: 'bg-[#DBEAFE] hover:bg-[#DBEAFE] text-[#1D4ED8]',
  exemption: 'bg-[#DCFCE7] hover:bg-[#DCFCE7] text-[#166534]',
  technical_failure: 'bg-[#DCFCE7] hover:bg-[#DCFCE7] text-[#166534]',
  uncounted_sub_request: 'bg-slate-100 hover:bg-slate-100 text-slate-600',
};

export const AttendancePolicyV2HistoryEventDotColorMap: Record<AttendancePolicyV2HistoryEventKind, string> = {
  no_show: 'bg-[#DC2626]',
  late_entry: 'bg-[#EA580C]',
  early_exit: 'bg-[#EA580C]',
  regular_sub_request: 'bg-[#1D4ED8]',
  short_notice_sub_request: 'bg-[#1D4ED8]',
  exemption: 'bg-[#166534]',
  technical_failure: 'bg-[#166534]',
  uncounted_sub_request: 'bg-slate-400',
};
